import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type FocusEvent,
} from "react";
import clsx from "clsx";
import { GdRichTextToolbarButton } from "@/components/ui/GdRichText";
import { SegmentedControl } from "@/components/ui/SegmentedControl";

export type HtmlMailBodyMode = "visual" | "code";

export interface HtmlMailBodyInsertHandle {
  insertText: (text: string) => void;
  focus: () => void;
  /** Aktuellen HTML-Stand (auch aus der visuellen Ansicht) lesen. */
  getHtml: () => string;
}

type HtmlMailBodyEditorProps = {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  minRows?: number;
  placeholder?: string;
  id?: string;
  className?: string;
  /** Standard: Anzeige (WYSIWYG). */
  defaultMode?: HtmlMailBodyMode;
  "aria-label"?: string;
};

export const HtmlMailBodyEditor = forwardRef<HtmlMailBodyInsertHandle, HtmlMailBodyEditorProps>(
  function HtmlMailBodyEditor(
    {
      value,
      onChange,
      disabled,
      minRows = 8,
      placeholder,
      id,
      className,
      defaultMode = "visual",
      "aria-label": ariaLabel,
    },
    ref,
  ) {
    const [mode, setMode] = useState<HtmlMailBodyMode>(defaultMode);
    const editorRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const isFocusedRef = useRef(false);
    const lastHtmlRef = useRef(value || "");
    const savedRangeRef = useRef<Range | null>(null);

    const emitChange = useCallback(
      (next: string) => {
        if (next === lastHtmlRef.current) return;
        lastHtmlRef.current = next;
        onChange(next);
      },
      [onChange],
    );

    const readVisualHtml = useCallback(() => {
      return editorRef.current?.innerHTML ?? lastHtmlRef.current;
    }, []);

    const saveSelection = useCallback(() => {
      const el = editorRef.current;
      if (!el) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!el.contains(range.commonAncestorContainer)) return;
      savedRangeRef.current = range.cloneRange();
    }, []);

    const restoreSelection = useCallback(() => {
      const el = editorRef.current;
      const sel = window.getSelection();
      if (!el || !sel) return false;
      el.focus();
      if (savedRangeRef.current) {
        try {
          sel.removeAllRanges();
          sel.addRange(savedRangeRef.current);
          return true;
        } catch {
          // Range ungültig nach DOM-Sync
        }
      }
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    }, []);

    useLayoutEffect(() => {
      if (mode !== "visual") return;
      const el = editorRef.current;
      if (!el || isFocusedRef.current) return;
      const next = value || "";
      if (next === lastHtmlRef.current && el.innerHTML === next) return;
      lastHtmlRef.current = next;
      el.innerHTML = next;
      savedRangeRef.current = null;
    }, [mode, value]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (mode === "code") {
            textareaRef.current?.focus();
            return;
          }
          editorRef.current?.focus();
        },
        getHtml: () => {
          if (mode === "visual") return readVisualHtml();
          return value || "";
        },
        insertText: (text: string) => {
          if (!text || disabled) return;
          if (mode === "code") {
            const input = textareaRef.current;
            const current = value || "";
            if (!input) {
              emitChange(`${current}${text}`);
              return;
            }
            const start = input.selectionStart ?? current.length;
            const end = input.selectionEnd ?? start;
            const next = `${current.slice(0, start)}${text}${current.slice(end)}`;
            emitChange(next);
            requestAnimationFrame(() => {
              input.focus();
              const pos = start + text.length;
              input.setSelectionRange(pos, pos);
            });
            return;
          }
          const el = editorRef.current;
          if (!el) return;
          restoreSelection();
          document.execCommand("insertText", false, text);
          emitChange(readVisualHtml());
          saveSelection();
        },
      }),
      [disabled, emitChange, mode, readVisualHtml, restoreSelection, saveSelection, value],
    );

    function switchMode(next: HtmlMailBodyMode) {
      if (next === mode) return;
      if (mode === "visual") {
        const html = readVisualHtml();
        lastHtmlRef.current = html;
        onChange(html);
      }
      setMode(next);
    }

    function applyFormat(command: "bold" | "italic" | "underline") {
      if (disabled || mode !== "visual") return;
      editorRef.current?.focus();
      restoreSelection();
      document.execCommand(command, false);
      emitChange(readVisualHtml());
      saveSelection();
    }

    function handleVisualInput() {
      emitChange(readVisualHtml());
      saveSelection();
    }

    function handleVisualBlur(_event: FocusEvent<HTMLDivElement>) {
      saveSelection();
      isFocusedRef.current = false;
      emitChange(readVisualHtml());
    }

    function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
      // Erlaubt HTML aus der Zwischenablage; Fallback auf Klartext.
      const html = event.clipboardData.getData("text/html");
      if (html) return;
      event.preventDefault();
      const text = event.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
      emitChange(readVisualHtml());
      saveSelection();
    }

    return (
      <div className={clsx("space-y-2", className)}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl
            name="Mail-Ansicht"
            value={mode}
            onChange={switchMode}
            disabled={disabled}
            options={[
              { value: "visual", label: "Anzeige" },
              { value: "code", label: "Code" },
            ]}
          />
        </div>

        {mode === "visual" ? (
          <div className="gd-rich-text">
            <div
              className="gd-rich-text__toolbar"
              role="toolbar"
              aria-label="Textformatierung"
              onMouseDown={(event) => event.preventDefault()}
            >
              <GdRichTextToolbarButton
                label="B"
                title="Fett"
                activeClassName="font-bold"
                disabled={disabled}
                onClick={() => applyFormat("bold")}
              />
              <GdRichTextToolbarButton
                label="I"
                title="Kursiv"
                activeClassName="italic"
                disabled={disabled}
                onClick={() => applyFormat("italic")}
              />
              <GdRichTextToolbarButton
                label="U"
                title="Unterstrichen"
                activeClassName="underline"
                disabled={disabled}
                onClick={() => applyFormat("underline")}
              />
            </div>
            <div
              id={id}
              ref={editorRef}
              className={clsx(
                "gd-rich-text__editor eh-mail-html-editor",
                disabled && "gd-rich-text__editor--disabled",
              )}
              contentEditable={!disabled}
              suppressContentEditableWarning
              role="textbox"
              aria-label={ariaLabel || "Nachricht"}
              aria-multiline
              data-placeholder={placeholder}
              style={{ ["--gd-rich-text-min-rows" as string]: String(minRows) } as const}
              onFocus={() => {
                isFocusedRef.current = true;
              }}
              onBlur={handleVisualBlur}
              onMouseUp={saveSelection}
              onKeyUp={saveSelection}
              onMouseDown={(event) => event.stopPropagation()}
              onInput={handleVisualInput}
              onPaste={handlePaste}
            />
          </div>
        ) : (
          <textarea
            id={id}
            ref={textareaRef}
            value={value}
            disabled={disabled}
            aria-label={ariaLabel || "Nachricht (HTML)"}
            placeholder={placeholder}
            rows={minRows}
            spellCheck={false}
            className="eh-mail-html-editor eh-mail-html-editor--code w-full resize-y font-mono text-xs leading-relaxed"
            onChange={(event) => emitChange(event.target.value)}
          />
        )}
      </div>
    );
  },
);
